import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import moment from "moment";
import API from "../../services/SchoolClassService";
import SettingsService from "../../services/SettingsService";
import * as teacherActions from "../../redux/actions/teacherActions";
import * as lessonRegisterActions from "../../redux/actions/lessonRegisterActions";
import * as schoolClassActions from "../../redux/actions/schoolClassActions";
import * as studentActions from "../../redux/actions/studentActions";
import * as classStudentsActions from "../../redux/actions/classStudentsActions";
import * as bookActions from "../../redux/actions/bookActions";
import { STATUS, CLASS_TYPES, WEEKDAYS } from "../common/Const";
import { buildSchoolYearRange, getCurrentSchoolYearStartYear } from "../common/schoolYear";
import DashboardCalendar from "./DashboardCalendar";
import SelectorModal from "../common/SelectorModal";
import CreateClassModal from "./CreateClassModal";
import {
  DEFAULT_DASHBOARD_RETURN_STATE_TTL_HOURS,
  readDashboardState,
  writeDashboardState,
} from "./dashboardStateStorage";

const isActiveTeacher = teacher => teacher && teacher.archived !== true && teacher.deleted !== true;
const getDefaultDashboardView = () => (typeof window !== "undefined" && window.innerWidth < 768 ? "day" : "week");

const compareTeachersByName = (left, right) => (
  (left.lastName || "").localeCompare(right.lastName || "", "pl", { sensitivity: "base" }) ||
  (left.firstName || "").localeCompare(right.firstName || "", "pl", { sensitivity: "base" })
);

const formatTeacherOption = teacher => ({
  value: teacher.id,
  text: `${teacher.firstName} ${teacher.lastName}`,
});

const getActiveTeachers = teachers => teachers
  .filter(isActiveTeacher)
  .sort(compareTeachersByName);

export const Dashboard = ({
  userId,
  userRole,
  userTeacherId,
  schoolClasses,
  loadSchoolClasses,
  saveSchoolClass,
  teachers,
  loadTeachers,
  students,
  loadStudents,
  classStudents,
  loadClassStudents,
  saveLessonRegister,
  loadLessonRegisters,
  deleteLessonRegister,
  lessonRegister,
  books,
  loadBooks,
  activeSchoolYearStartYear,
  onActiveSchoolYearChange,
}) => {
  const activeSchoolYearRange = buildSchoolYearRange(activeSchoolYearStartYear);
  const canManageDashboardClasses = userRole !== "TEACHER";
  const [dashboardStateTtlHours, setDashboardStateTtlHours] = useState(DEFAULT_DASHBOARD_RETURN_STATE_TTL_HOURS);
  const [restoredDashboardState, setRestoredDashboardState] = useState(() => readDashboardState(dashboardStateTtlHours));
  const [user, setUser] = useState({});
  const [selectedTeacher, setSelectedTeacher] = useState(user);
  const [calendarView, setCalendarView] = useState(restoredDashboardState?.view || getDefaultDashboardView());
  const [calendarDate, setCalendarDate] = useState(restoredDashboardState?.date || new Date());
  const [scheduledClasses, setScheduledClasses] = useState([]);
  const [status, setStatus] = useState(STATUS.VIEW);
  const [errors, setErrors] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedSlotData, setSelectedSlotData] = useState(null);
  const activeTeachers = getActiveTeachers(teachers);
  const isLessonWithinActiveSchoolYear = (scheduledLesson) => {
    const lessonDate = scheduledLesson.lessonRegister
      ? scheduledLesson.lessonRegister.lessonDetails.date
      : scheduledLesson.defaultLessonDetails.date;

    return lessonDate >= activeSchoolYearRange.fromDate && lessonDate <= activeSchoolYearRange.tillDate;
  };

  useEffect(() => {
    loadSchoolClasses(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate).catch((error) => {
      alert(`Loading schoolClasses failed ${error}`);
    });
  }, [activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate, loadSchoolClasses]);

  useEffect(() => {
    if (teachers.length === 0) {
      loadTeachers().catch((error) => {
        alert(`Loading teachers failed ${error}`);
      });
    }
  }, []);

  useEffect(() => {
    try {
      SettingsService.getSettings()
        .then((settings) => {
          const dashboardPreferences = settings.find(setting => setting.id === "dashboardPreferences");
          if (dashboardPreferences) {
            const nextTtlHours = dashboardPreferences.returnStateTtlHours;
            const nextRestoredState = readDashboardState(nextTtlHours);

            setDashboardStateTtlHours(nextTtlHours);
            setRestoredDashboardState(nextRestoredState);
            setCalendarView(nextRestoredState?.view || getDefaultDashboardView());
            setCalendarDate(nextRestoredState?.date || new Date());
          }
        })
        .catch(() => {});
    } catch (error) {
      // Dashboard state TTL falls back to the built-in default when settings are unavailable.
    }
  }, []);

  useEffect(() => {
    const teacher = userRole === "TEACHER" && userTeacherId
      ? teachers.find(t => t.id === userTeacherId) || teachers.find(t => t.email === userId)
      : teachers.find(t => t.email === userId);
    if (teacher) {
      setUser(teacher);
      const restoredTeacher = canManageDashboardClasses && restoredDashboardState && restoredDashboardState.teacherId
        ? getActiveTeachers(teachers).find(t => t.id === restoredDashboardState.teacherId)
        : null;
      const teacherToSelect = restoredTeacher || teacher;

      setSelectedTeacher({ value: teacherToSelect.id, text: `${teacherToSelect.firstName} ${teacherToSelect.lastName}` });
    }
  }, [canManageDashboardClasses, restoredDashboardState, teachers, userId, userRole, userTeacherId]);

  useEffect(() => {
    loadLessonRegisters(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate).catch((error) => {
      alert(`Loading lessonRegisters failed ${error}`);
    });
  }, [activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate, loadLessonRegisters]);

  useEffect(() => {
    loadClassStudents(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate).catch((error) => {
      alert(`Loading classStudents failed ${error}`);
    });
  }, [activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate, loadClassStudents]);

  useEffect(() => {
    if (students.length === 0) {
      loadStudents().catch((error) => {
        alert(`Loading students failed ${error}`);
      });
    }
  }, []);

  useEffect(() => {
    if (canManageDashboardClasses && books.length === 0) {
      loadBooks().catch((error) => {
        alert(`Loading books failed ${error}`);
      });
    }
  }, [books.length, canManageDashboardClasses, loadBooks]);

  useEffect(() => {
    const updateScheduledClasses = () => {
      const filteredClasses = schoolClasses.filter((schoolClass) => {
        const classFrom = schoolClass.fromDate;
        const classTill = schoolClass.tillDate || schoolClass.fromDate;

        if (classTill < activeSchoolYearRange.fromDate) return false;
        if (classFrom > activeSchoolYearRange.tillDate) return false;

        return schoolClass.teacherId === selectedTeacher.value;
      });

      const generatedClasses = filteredClasses.map((schoolClass) => {
        const scheduledLessons = API.generateScheduledClasses(
          schoolClass,
          lessonRegister,
          students,
          classStudents,
        ).filter(isLessonWithinActiveSchoolYear);

        return {
          scheduledLessons,
        };
      }).filter(scheduledClass => scheduledClass.scheduledLessons.length > 0);
      setScheduledClasses(generatedClasses);
    };

    updateScheduledClasses();
  }, [
    activeSchoolYearRange.fromDate,
    activeSchoolYearRange.tillDate,
    classStudents,
    lessonRegister,
    schoolClasses,
    selectedTeacher,
    students,
  ]);

  const handleTeacherSelect = (option) => {
    setSelectedTeacher(option);
    setIsModalOpen(false);
    writeDashboardState({ teacherId: option.value, view: calendarView, date: calendarDate });
  };

  const handleRegisterUpdate = async (event) => {
    saveLessonRegister(event.lessonRegister)
      .then((savedRegister) => {
        setStatus(STATUS.VIEW);
      })
      .catch((error) => {
        setErrors({ ...errors, saveError: error.message });
        setStatus(STATUS.SUBMITTED);
      });
  };

  const handleCreateClass = (classData) => {
    const classPayload = {
      ...classData,
      fromDate: classData.fromDate || moment().format('YYYY-MM-DD'),
    };
    
    return saveSchoolClass(classPayload)
      .then(() => {
        loadSchoolClasses(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate);
        setIsCreateModalOpen(false);
      });
  };

  const handleVisibleDateChange = (visibleDate) => {
    setCalendarDate(visibleDate);
    writeDashboardState({ teacherId: selectedTeacher.value, view: calendarView, date: visibleDate });
    const nextSchoolYearStartYear = getCurrentSchoolYearStartYear(visibleDate);

    if (nextSchoolYearStartYear !== activeSchoolYearStartYear) {
      onActiveSchoolYearChange(nextSchoolYearStartYear);
    }
  };

  const handleViewChange = (view) => {
    setCalendarView(view);
    writeDashboardState({ teacherId: selectedTeacher.value, view, date: calendarDate });
  };

  const handleDashboardActivity = () => {
    writeDashboardState({ teacherId: selectedTeacher.value, view: calendarView, date: calendarDate });
  };

  return (
    <div
      className="mb-5"
      onClick={handleDashboardActivity}
      onKeyDown={handleDashboardActivity}
      role="presentation"
    >
      {canManageDashboardClasses && (
      <h4 className="col-block d-flex align-items-center mb-3">
        Oglądasz: &nbsp;
        {selectedTeacher.text || `${userId}`}
        <button
          type="button"
          className="manage-select-button"
          onClick={() => setIsModalOpen(true)}
        >
          Wybierz nauczyciela
        </button>
      </h4>
      )}
      {canManageDashboardClasses && (
      <SelectorModal
        title="Wybierz nauczyciela"
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        values={activeTeachers.map(formatTeacherOption)}
        onSelect={option => handleTeacherSelect(option)}
        selectedIDs={[selectedTeacher.value || user.id]}
        layout="grid"
      />
      )}
      <DashboardCalendar
        scheduledClasses={scheduledClasses}
        teachers={teachers}
        students={students}
        onSubmit={handleRegisterUpdate}
        deleteLessonRegister={deleteLessonRegister}
        view={calendarView}
        date={calendarDate}
        onViewChange={handleViewChange}
        onVisibleDateChange={handleVisibleDateChange}
        userRole={userRole}
        userTeacherId={userTeacherId}
      />
      <CreateClassModal
        isOpen={isCreateModalOpen}
        initialDate={selectedSlotData?.date}
        initialTime={{ start: selectedSlotData?.startTime, end: selectedSlotData?.endTime }}
        initialDayOfWeek={selectedSlotData?.dayOfWeek}
        initialTeacherId={selectedTeacher.value}
        classTypes={CLASS_TYPES}
        teachers={activeTeachers}
        books={books}
        weekdays={WEEKDAYS}
        onClose={() => setIsCreateModalOpen(false)}
        onSave={handleCreateClass}
      />
    </div>
  );
};

const mapStateToProps = state => ({
  schoolClasses: state.schoolClasses,
  teachers: state.teachers,
  students: state.students,
  classStudents: state.classStudents,
  lessonRegister: state.lessonRegister,
  books: state.books,
  activeSchoolYearStartYear: state.settings.activeSchoolYearStartYear,
});

const mapDispatchToProps = {
  loadSchoolClasses: schoolClassActions.loadSchoolClasses,
  saveSchoolClass: schoolClassActions.saveSchoolClass,
  loadBooks: bookActions.loadBooks,
  loadClassStudents: classStudentsActions.loadClassStudents,
  loadTeachers: teacherActions.loadTeachers,
  loadStudents: studentActions.loadStudents,
  saveLessonRegister: lessonRegisterActions.saveLessonRegister,
  loadLessonRegisters: lessonRegisterActions.loadLessonRegisters,
  deleteLessonRegister: lessonRegisterActions.deleteLessonRegister,
};

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
